<template>
	<!-- Row layout (desktop): the section as a dropdown in the toolbar. -->
	<Popover v-if="dropdown" v-slot="{ open }" class="relative filter-dropdown">
		<PopoverButton
			:id="`${sectionId}-button`"
			class="flex items-center gap-1 text-sm text-primary filter-dropdown__toggle"
		>
			<span :id="`${sectionId}-title`" class="capitalize">
				{{ getFilterName(name) }}
			</span>
			<template v-if="activeLabels.length > 1">
				<span
					class="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-gray-200 text-xs leading-5 text-center filter-dropdown__count"
					aria-hidden="true"
					>{{ activeLabels.length }}</span
				>
				<span class="sr-only">{{ activeLabels.join(", ") }}</span>
			</template>
			<span
				v-else-if="activeLabels.length"
				class="max-w-48 truncate filter-dropdown__value"
			>
				{{ activeLabels[0] }}
			</span>
			<ChevronDownIcon
				class="h-5 w-5 transition-transform"
				:class="{ 'rotate-180': open }"
				aria-hidden="true"
			/>
		</PopoverButton>
		<PopoverPanel
			:id="`${sectionId}-panel`"
			role="group"
			:aria-labelledby="`${sectionId}-title`"
			class="absolute left-0 z-20 mt-3 w-72 max-h-96 overflow-y-auto bg-white p-4 shadow-lg ring-1 ring-black/5 filter-dropdown__panel"
		>
			<component :is="component" v-model="parentValue" :filters="filters" />
		</PopoverPanel>
	</Popover>
	<div v-else>
		<Disclosure
			v-slot="{ open }"
			as="div"
			class="border-t border-t-gray-dark px-4 lg:px-0 filter-section"
			:default-open="!!isOpen"
		>
			<DisclosureButton
				:id="`${sectionId}-button`"
				class="flex items-center justify-between font-medium py-4 w-full filter-section__toggle"
			>
				<span
					:id="`${sectionId}-title`"
					class="text-base font-medium capitalize text-primary filter-section__title"
				>
					{{ getFilterName(name) }}
				</span>
				<span class="ml-6 flex items-center">
					<ChevronDownIcon
						v-if="!open"
						class="block h-6 w-6 text-gray-900"
						aria-hidden="true"
					/>
					<ChevronUpIcon
						v-else
						class="block h-6 w-6 text-gray-900"
						aria-hidden="true"
					/>
				</span>
			</DisclosureButton>
			<transition
				enter-active-class="transition duration-200 ease-out"
				enter-from-class="opacity-0"
				enter-to-class="opacity-100"
				leave-active-class="transition duration-200 ease-out"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
			>
				<!-- A group named by the title, for the options or price fields inside. -->
				<DisclosurePanel
					:id="`${sectionId}-panel`"
					role="group"
					:aria-labelledby="`${sectionId}-title`"
					class="pt-2"
				>
					<component :is="component" v-model="parentValue" :filters="filters" />
				</DisclosurePanel>
			</transition>
		</Disclosure>
	</div>
</template>

<script setup lang="ts">
import {
	Disclosure,
	DisclosureButton,
	DisclosurePanel,
	Popover,
	PopoverButton,
	PopoverPanel
} from "@headlessui/vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/24/outline";
import { capitalize, defineAsyncComponent, computed } from "vue";
import type { SelectedFilter } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import { storeToRefs } from "pinia";
import type { ParametricListInfo } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { getBooleanFilterName } from "~/utils/categories/getBooleanFilterName";
import { getActiveFilterLabels } from "~/utils/categories/activeFilterLabels";
import {
	isFilterSectionOpenByDefault,
	isFilterSectionSelected,
	type FilterSectionValue
} from "~/utils/categories/filterSections";
import { useListingFiltersConfig } from "~/layers/base/composables/useListingFiltersConfig";

interface Props {
	name: string;
	filters?: FilterSectionValue;
	modelValue?: SelectedFilter;
	initialSelectedFilters?: SelectedFilter;
	/** Render as a toolbar dropdown instead of an accordion section. */
	dropdown?: boolean;
}

const STATIC_FILTER_NAMES: Record<string, string> = {
	category: "Category",
	brands: "filter_brands_title",
	price: "Price",
	stock: "Stock",
	flags: "Flags"
};

const { name, modelValue, filters, dropdown = false } = defineProps<Props>();

/** Per instance: the desktop list and the mobile drawer render at once. */
const sectionId = `filter-${useId()}`;

const { parametricsInformation } = storeToRefs(useProductStore());
const { t } = useI18n();
const norceStore = useNorceStore();

/** Dropdown only: the group's selected values, shown on its button. */
const activeLabels = computed(() =>
	dropdown
		? getActiveFilterLabels(name, filters, modelValue, {
				inStock: t("in_stock"),
				yes: t("Yes"),
				currency: norceStore.currentCurrency
			})
		: []
);

const parametricsMap = computed(() => {
	const map = new Map<string, ParametricListInfo>();
	if (parametricsInformation.value) {
		parametricsInformation.value.forEach((param) => {
			if (param.Code) {
				map.set(param.Code.toLowerCase(), param);
			}
		});
	}
	return map;
});
const getFilterName = (filterKey: string): string => {
	const key = filterKey.toLowerCase();

	if (STATIC_FILTER_NAMES[key]) {
		return t(STATIC_FILTER_NAMES[key]);
	}

	const booleanName = getBooleanFilterName(filters);
	if (booleanName) return booleanName;

	return parametricsMap.value.get(key)?.Name ?? capitalize(filterKey);
};

const component = computed(() =>
	defineAsyncComponent(() => {
		if (
			name === Filters.MANUFACTURER ||
			name === Filters.PRICE ||
			name === Filters.COLOR ||
			name === Filters.CATEGORY ||
			name === Filters.ONHAND ||
			name === Filters.FLAG
		) {
			return import(
				`~/layers/base/components/listing-filters/${capitalize(name)}.vue`
			);
		} else {
			return import("~/layers/base/components/listing-filters/Parametrics.vue");
		}
	})
);

const emit = defineEmits(["update:modelValue"]);
const parentValue = computed({
	get() {
		return modelValue;
	},
	set(nextValue) {
		emit("update:modelValue", nextValue);
	}
});
const { openSections } = useListingFiltersConfig();

/** Open on load: the template asks for it, or it has a selected value. */
const isOpen = computed(
	() =>
		isFilterSectionOpenByDefault(name, openSections) ||
		isFilterSectionSelected(name, filters, modelValue)
);
</script>
